/**
 * EditMeetingDialog Component
 * Form dialog for editing existing meetings and updating status
 */

import { FolderOpen, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateMeeting } from "@/hooks/useCustomerMeetings";
import type { EventType, Meeting, MeetingStatus, MeetingType } from "@/types/meeting.types";

interface EditMeetingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  meeting: Meeting | null;
}

export function EditMeetingDialog({ isOpen, onClose, meeting }: EditMeetingDialogProps) {
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingTime, setMeetingTime] = useState("");
  const [eventType, setEventType] = useState<EventType>("meeting");
  const [meetingType, setMeetingType] = useState<MeetingType>("consultation");
  const [status, setStatus] = useState<MeetingStatus>("scheduled");
  const [duration, setDuration] = useState("60");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");

  const updateMeeting = useUpdateMeeting();
  const navigate = useNavigate();

  // Populate form when meeting changes
  useEffect(() => {
    if (meeting) {
      setMeetingDate(meeting.meeting_date);
      setMeetingTime(meeting.meeting_time);
      setEventType(meeting.event_type || "meeting");
      setMeetingType(meeting.meeting_type);
      setStatus(meeting.status);
      setDuration(String(meeting.duration_minutes));
      setLocation(meeting.location || "");
      setNotes(meeting.notes || "");
    }
  }, [meeting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!meeting) {
      return;
    }

    // Validate duration
    const durationMinutes = Number.parseInt(duration);
    if (durationMinutes < 15 || durationMinutes > 480) {
      alert("Duration must be between 15 and 480 minutes.");
      return;
    }

    try {
      await updateMeeting.mutateAsync({
        meetingId: meeting.id,
        updates: {
          meeting_date: meetingDate,
          meeting_time: meetingTime,
          event_type: eventType,
          meeting_type: eventType === "meeting" ? meetingType : "consultation",
          status: status,
          duration_minutes: durationMinutes,
          location: location.trim() || null,
          notes: notes.trim() || null,
        },
      });

      onClose();
    } catch (error) {
      // Error is handled by the mutation hook with toast
      console.error("Failed to update meeting:", error);
    }
  };

  if (!meeting) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Meeting</DialogTitle>
          <DialogDescription>
            Update meeting details or change the status. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        {/* Navigation Buttons */}
        <div className="flex gap-2 -mt-2 mb-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              onClose();
              navigate(`/customers/${meeting.customer_id}`);
            }}
            className="flex-1"
          >
            <User className="h-3.5 w-3.5 mr-1.5" />
            View Customer
          </Button>
          {meeting.project_id && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                onClose();
                navigate(`/projects/${meeting.project_id}`);
              }}
              className="flex-1"
            >
              <FolderOpen className="h-3.5 w-3.5 mr-1.5" />
              View Project
            </Button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">
              Status <span className="text-destructive">*</span>
            </Label>
            <Select value={status} onValueChange={(v) => setStatus(v as MeetingStatus)}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="rescheduled">Rescheduled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Event Type */}
          <div className="space-y-2">
            <Label htmlFor="event-type">
              Event Type <span className="text-destructive">*</span>
            </Label>
            <Select value={eventType} onValueChange={(v) => setEventType(v as EventType)}>
              <SelectTrigger id="event-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="task">Task</SelectItem>
                <SelectItem value="reminder">Reminder</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Meeting Type (only show if event_type = 'meeting') */}
          {eventType === "meeting" && (
            <div className="space-y-2">
              <Label htmlFor="meeting-type">
                Meeting Type <span className="text-destructive">*</span>
              </Label>
              <Select value={meetingType} onValueChange={(v) => setMeetingType(v as MeetingType)}>
                <SelectTrigger id="meeting-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consultation">Consultation</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                  <SelectItem value="project-review">Project Review</SelectItem>
                  <SelectItem value="quote-discussion">Quote Discussion</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="meeting-date">
                Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="meeting-date"
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                required={true}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meeting-time">
                Time <span className="text-destructive">*</span>
              </Label>
              <Input
                id="meeting-time"
                type="time"
                value={meetingTime}
                onChange={(e) => setMeetingTime(e.target.value)}
                required={true}
              />
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration">
              Duration (minutes) <span className="text-destructive">*</span>
            </Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger id="duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="180">3 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              placeholder="e.g., Customer site, Office, Video call"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Meeting notes, outcomes, next steps..."
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {status === "completed" && (
              <p className="text-sm text-muted-foreground">
                Add meeting notes and outcomes for completed meetings.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMeeting.isPending}>
              {updateMeeting.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
